import React, { useEffect, useState } from "react";
import { notification } from "antd";
import { IoMailOutline, IoLockClosedOutline, IoArrowBackOutline, IoEyeOutline, IoEyeOffOutline } from "react-icons/io5";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../../utils/config";
import OTPInput from "react-otp-input";
import logo from '../../assests/images/weblogo.png';
const STORAGE_KEY = "forgotPasswordState";
const STORAGE_TTL_MS = 5 * 60 * 1000; // roughly how long a reset code stays valid

const readSavedState = () => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "null");
    if (!saved || Date.now() - saved.savedAt > STORAGE_TTL_MS) return null;
    return saved;
  } catch {
    return null;
  }
};

const clearSavedState = () => sessionStorage.removeItem(STORAGE_KEY);

export default function ForgotPassword() {
  const [userData, setUserData] = useState(() => {
    const saved = readSavedState();
    return { email: saved?.email || "", password: "", reset_code: "" };
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(() => {
    const saved = readSavedState();
    return saved?.step || { first: true, second: false, third: false };
  });

  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const userDataLocal = localStorage.getItem("user");
  const timeStamp = localStorage.getItem("timestamp");
  const isExpired = timeStamp && Number(timeStamp) < Date.now();
  let user = null;
  try {
    user = userDataLocal ? JSON.parse(userDataLocal) : null;
  } catch {
    user = null;
  }

  useEffect(() => {
    if (isExpired) {
      localStorage.removeItem("token");
      localStorage.removeItem("timestamp");
      localStorage.removeItem("user");
      return;
    }
    if (token && user) {
      if (user?.type === "admin" || user?.type === "agent") {
        navigate("/admin");
      } else if (user?.type === "user") {
        navigate("/user");
      }
    }
  }, []);

  useEffect(() => {
    if (step.third) {
      // No point resuming into the password step after a reload — the code
      // itself isn't persisted, so a restored "third" step can never submit.
      clearSavedState();
      return;
    }
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ step, email: userData.email, savedAt: Date.now() })
    );
  }, [step, userData.email]);

  const handleForgotEmail = async () => {
    if (!loading) {
      setLoading(true);
      try {
        const response = await axios.post(`${BASE_URL}send-reset-code`, { email: userData?.email });
        if (response) {
          setStep({ second: true });
          notification.success({ message: response?.data?.message });
          setLoading(false);
        }
      } catch (error) {
        notification.error({ message: error?.response?.data?.message });
        setLoading(false);
      }
    }
  };

  const handleForgot = async () => {
    if (!loading) {
      setLoading(true);
      try {
        const response = await axios.post(`${BASE_URL}reset-password`, { ...userData });
        if (response) {
          clearSavedState();
          notification.success({ message: response?.data?.message });
          navigate("/login");
          setLoading(false);
        }
      } catch (error) {
        notification.error({ message: error?.response?.data?.message || "Reset failed, please re-enter your code" });
        setStep({ second: true });
        setUserData((prev) => ({ ...prev, reset_code: "" }));
        setLoading(false);
      }
    }
  };

  // Step meta for header text
  const stepMeta = {
    first: {
      badge: "Step 1 of 3",
      title: "Forgot password?",
      sub: "Enter the email address associated with your account and we'll send you a reset code.",
    },
    second: {
      badge: "Step 2 of 3",
      title: "Check your email",
      sub: `We sent a 6-digit code to ${userData.email || "your email"}. Enter it below to continue.`,
    },
    third: {
      badge: "Step 3 of 3",
      title: "New password",
      sub: "Choose a strong password that's at least 8 characters long.",
    },
  };

  const currentStep = step.third ? "third" : step.second ? "second" : "first";
  const meta = stepMeta[currentStep];

  const features = [
    "Secure multi-wallet management",
    "Real-time balance tracking",
    "Instant crypto & fiat conversions",
  ];

  if (token && user && !isExpired) return null;

  return (
    <div className="min-h-screen flex bg-[#080810]">

      {/* ── LEFT PANEL ── */}
      <div className="hidden lg:flex w-[46%] min-h-screen relative flex-col p-10 gap-10 overflow-hidden bg-[#0c0b18]">

        {/* Grid bg */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(109,40,217,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(109,40,217,0.07) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
          }}
        />
        {/* Radial glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 90% 55% at 50% -5%, rgba(109,40,217,0.38) 0%, transparent 70%), radial-gradient(ellipse 60% 70% at 90% 90%, rgba(192,38,211,0.18) 0%, transparent 60%)",
          }}
        />

        {/* Logo */}
        <div className="relative z-10 flex items-center gap-2.5">
           <Link to="/">
            <img src={logo} alt="Ellipticline Logo" className="h-11 w-auto object-contain" />
          </Link>
        </div>

        {/* Body */}
        <div className="relative z-10 flex flex-col gap-8 flex-1 justify-center">

          <div className="flex flex-col gap-3">
            <h1 className="text-4xl xl:text-5xl font-extrabold text-white leading-[1.08] tracking-tight">
              Account{" "}
              <span className="bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
                recovery.
              </span>
            </h1>
            <p className="text-sm text-white/80 leading-relaxed max-w-sm">
              Regain access to your portfolio in just a few quick steps. Your funds are safe.
            </p>
          </div>

          {/* Features */}
          <div className="flex flex-col gap-3">
            {features.map((f) => (
              <div key={f} className="flex items-center gap-3 text-white/50 text-sm">
                <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex-shrink-0" />
                {f}
              </div>
            ))}
          </div>

          {/* Step progress indicator */}
          <div className="flex flex-col gap-2">
            <p className="text-[11px] uppercase tracking-widest text-white mb-1">Recovery progress</p>
            <div className="flex gap-2">
              {["Email", "Verify code", "New password"].map((label, i) => {
                const stepIndex = step.third ? 2 : step.second ? 1 : 0;
                const isActive = i === stepIndex;
                const isDone = i < stepIndex;
                return (
                  <div key={label} className="flex flex-col gap-1.5 flex-1">
                    <div
                      className={`h-1 rounded-full transition-all duration-300 ${
                        isDone
                          ? "bg-violet-500"
                          : isActive
                          ? "bg-gradient-to-r from-violet-500 to-fuchsia-500"
                          : "bg-white/10"
                      }`}
                    />
                    <span
                      className={`text-[10px] tracking-wide ${
                        isActive ? "text-violet-400" : isDone ? "text-white/80" : "text-white/20"
                      }`}
                    >
                      {label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

        </div>
      </div>

      {/* ── RIGHT PANEL ── */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 lg:border-l lg:border-white/5">
        <div className="w-full max-w-md">

          {/* Mobile logo */}
          <div className="flex lg:hidden items-center gap-2.5 mb-8">
            <Link to="/">
            <img src={logo} alt="Ellipticline Logo" className="h-11 w-auto object-contain" />
          </Link>
          </div>

          {/* Back button */}
          <Link
            to="/login"
            onClick={clearSavedState}
            className="inline-flex items-center gap-2 text-white/80 hover:text-white/70 transition-colors text-sm mb-8 group"
          >
            <IoArrowBackOutline className="text-[15px] group-hover:-translate-x-0.5 transition-transform" />
            Back to sign in
          </Link>

          {/* Header */}
          <div className="mb-8">
            <div className="inline-flex items-center gap-1.5 bg-violet-500/10 border border-violet-500/25 text-violet-400 text-[11px] font-medium tracking-wider px-3 py-1 rounded-full mb-4">
              {meta.badge}
            </div>
            <h2 className="text-[2rem] font-extrabold text-white tracking-tight leading-tight mb-2">
              {meta.title}
            </h2>
            <p className="text-sm text-white leading-relaxed">
              {meta.sub}
            </p>
          </div>

          {/* ── STEP 1: Email ── */}
          {step.first && (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-[12px] font-medium text-white tracking-wide">
                  Email address
                </label>
                <div className="relative">
                  <IoMailOutline className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white text-[15px] pointer-events-none" />
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={userData.email}
                    onChange={(e) => setUserData({ ...userData, email: e.target.value })}
                    onKeyDown={(e) => e.key === "Enter" && handleForgotEmail()}
                    className="w-full h-11 bg-white/[0.04] border border-white/[0.08] rounded-xl text-white text-sm pl-10 pr-4 outline-none placeholder:text-white/20 focus:border-violet-500/60 focus:bg-violet-500/[0.06] transition-all duration-200"
                  />
                </div>
              </div>

              <button
                disabled={loading || !userData.email}
                onClick={handleForgotEmail}
                className="w-full h-12 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-bold text-sm tracking-wide flex items-center justify-center gap-2.5 mt-1 hover:opacity-90 active:scale-[0.985] transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {loading && (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                )}
                {loading ? "Sending code…" : "Send reset code"}
              </button>
            </div>
          )}

          {/* ── STEP 2: OTP ── */}
          {step.second && (
            <div className="flex flex-col gap-6">
              <OTPInput
                value={userData.reset_code}
                onChange={(e) => setUserData({ ...userData, reset_code: e })}
                numInputs={6}
                containerStyle={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "10px",
                }}
                renderSeparator={null}
                renderInput={(props) => (
                  <input
                    {...props}
                    style={{
                      width: "48px",
                      height: "52px",
                      textAlign: "center",
                      fontSize: "20px",
                      fontWeight: "700",
                      color: "#fff",
                      background: "rgba(255,255,255,0.04)",
                      border: userData.reset_code.length > (props["data-index"] ?? 0)
                        ? "1px solid rgba(124,58,237,0.6)"
                        : "1px solid rgba(255,255,255,0.08)",
                      borderRadius: "12px",
                      outline: "none",
                      caretColor: "#a78bfa",
                      transition: "border-color 0.2s",
                    }}
                  />
                )}
              />

              <button
                disabled={loading || userData.reset_code.length < 6}
                onClick={() =>
                  userData.reset_code
                    ? setStep({ third: true })
                    : notification.error({ message: "Enter Code" })
                }
                className="w-full h-12 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-bold text-sm tracking-wide flex items-center justify-center gap-2.5 hover:opacity-90 active:scale-[0.985] transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Verify code
              </button>

              <button
                type="button"
                onClick={() => { setStep({ first: true }); setUserData({ ...userData, reset_code: "" }); }}
                className="text-center text-sm text-white/80 hover:text-white/50 transition-colors"
              >
                Didn't receive a code? <span className="text-violet-400">Resend</span>
              </button>
            </div>
          )}

          {/* ── STEP 3: New Password ── */}
          {step.third && (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-[12px] font-medium text-white tracking-wide">
                  New password
                </label>
                <div className="relative">
                  <IoLockClosedOutline className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white text-[15px] pointer-events-none" />
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Min. 8 characters"
                    value={userData.password}
                    onChange={(e) => setUserData({ ...userData, password: e.target.value })}
                    onKeyDown={(e) => e.key === "Enter" && handleForgot()}
                    className="w-full h-11 bg-white/[0.04] border border-white/[0.08] rounded-xl text-white text-sm pl-10 pr-11 outline-none placeholder:text-white/20 focus:border-violet-500/60 focus:bg-violet-500/[0.06] transition-all duration-200"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((p) => !p)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white hover:text-white/60 transition-colors cursor-pointer"
                    tabIndex={-1}
                  >
                    {showPassword
                      ? <IoEyeOffOutline className="text-[15px]" />
                      : <IoEyeOutline className="text-[15px]" />
                    }
                  </button>
                </div>
              </div>

              <button
                disabled={loading || !userData.password}
                onClick={handleForgot}
                className="w-full h-12 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-bold text-sm tracking-wide flex items-center justify-center gap-2.5 mt-1 hover:opacity-90 active:scale-[0.985] transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {loading && (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                )}
                {loading ? "Resetting…" : "Reset password"}
              </button>
            </div>
          )}

          {/* Footer */}
          <p className="text-center text-sm text-white/80 mt-8">
            Remembered it?{" "}
            <Link
              to="/login"
              onClick={clearSavedState}
              className="text-violet-400 font-semibold hover:text-violet-300 transition-colors"
            >
              Back to sign in
            </Link>
          </p>

        </div>
      </div>

    </div>
  );
}